import { useEffect, useMemo } from 'react';
import { useTracking } from 'react-tracking';

import loadable from '@loadable/component';
import { useNarrative } from '@/pages/Narrative/hooks/useNarrative';
import { Footer } from '@components/Footer';
import { Sentry } from '@/components/Sentry';
import useUser from '@/hooks/useUser';

import PageSkeleton from './components/PageSkeleton';
import { NarrativeSchema } from './components/NarrativeSchema';
import { setLastVisitedStockForMiniValuator } from '../../utils/storage';

import If from '@/components/If';
import { NarrativeNotFound } from '../NarrativeNotFound';
import { narrativeRetry } from '../../utils/error';
import { NarrativeLayout } from './components/NarrativeLayout';
import { NarrativeInvalidationBoundary } from '../NarrativeInvalidationBoundary';
import { handleOnSetPrimary } from './utils';
import { useNarrativeSlug } from '../../hooks/useNarrativeSlug';

const NarrativeGuestRegistrationModal = loadable(
  () => import('@/components/NarrativeGuestRegistrationModal'),
  {
    resolveComponent: (component) => component.NarrativeGuestRegistrationModal,
  }
);

export const Narrative = () => {
  const { slug } = useNarrativeSlug();
  const {
    data: narrativeResponse,
    isLoading,
    error,
  } = useNarrative(
    { slug, ref: slug },
    {
      retry: narrativeRetry,
    }
  );
  const narrative = narrativeResponse?.narrative;
  const publishedUpdates = useMemo(
    () =>
      narrativeResponse?.narrativeUpdates?.edges.map((edge) => edge.node) ?? [],
    [narrativeResponse?.narrativeUpdates?.edges]
  );
  const { data: user } = useUser();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'narrative',
    companyId: narrative?.companyId,
    uniqueSymbol: narrative?.company?.uniqueSymbol,
    narrativeId: narrative?.id,
    userId: user?.id,
    owner: {
      publicId: narrative?.owner.publicId,
      displayName: narrative?.owner.displayName,
      role: narrative?.owner.role,
    },
  });

  useEffect(() => {
    if (narrative?.id) {
      if (slug && !window.location.hash) {
        window.scrollTo(0, 0);
      }
      trackEvent({
        action: 'view',
      });
      if (narrative.company?.canonicalURL) {
        setLastVisitedStockForMiniValuator(narrative?.company?.canonicalURL);
      }
    }
  }, [narrative, slug, trackEvent]);

  const canShowRegistration = !user.loggedIn;

  if (isLoading) return <PageSkeleton />;

  if (error || !narrative) return <NarrativeNotFound />;

  return (
    <Track>
      <div className="relative mt-x0 w-full max-w-screen-xl md:mt-x3">
        <Sentry
          sentryCapture={{ team: 'narwhals', extra: { slug } }}
          globalDisabled
        >
          <NarrativeSchema narrative={narrative} />
          <NarrativeInvalidationBoundary
            onSetPrimary={handleOnSetPrimary(
              useNarrative.getOptions({ slug, ref: slug }).queryKey
            )}
          >
            <NarrativeLayout
              narrative={narrative}
              publishedUpdates={publishedUpdates}
            />
          </NarrativeInvalidationBoundary>
          <Footer />
          <If match={canShowRegistration}>
            <NarrativeGuestRegistrationModal />
          </If>
        </Sentry>
      </div>
    </Track>
  );
};
